/**
 * League lifecycle, shared by every league screen (LG-B10 / LG-B11).
 * Mirrors backend/src/constants/leagueStatus.js.
 *
 *   draft → registration_open → registration_closed → in_progress → completed
 *   any non-final status → cancelled
 *
 * "active" is the legacy name of in_progress: the season is running,
 * registration is closed and nobody new can join.
 */

export const LEAGUE_STATUS_LABELS = Object.freeze({
  draft: 'Draft',
  registration_open: 'Registration Open',
  registration_closed: 'Registration Closed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
});

export const LEAGUE_TRANSITIONS = Object.freeze({
  draft: ['registration_open', 'cancelled'],
  registration_open: ['draft', 'registration_closed', 'cancelled'],
  registration_closed: ['registration_open', 'in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
});

/** The main "next step" button per status (cancel is offered separately). */
export const NEXT_STEP = Object.freeze({
  draft: { label: 'Open Registration', nextStatus: 'registration_open', variant: 'success', icon: '📣' },
  registration_open: { label: 'Close Registration', nextStatus: 'registration_closed', variant: 'warning', icon: '🔒' },
  registration_closed: { label: 'Start League', nextStatus: 'in_progress', variant: 'primary', icon: '🚀' },
  in_progress: { label: 'Complete League', nextStatus: 'completed', variant: 'outline', icon: '🏁' },
});

export const normalizeLeagueStatus = (status) => (status === 'active' ? 'in_progress' : status || 'draft');

export const leagueStatusLabel = (status) => {
  const s = normalizeLeagueStatus(status);
  return LEAGUE_STATUS_LABELS[s] || String(s).replace(/_/g, ' ');
};

/** Invitations, join requests and accepting them. */
export const isRegistrationOpen = (league) => normalizeLeagueStatus(league?.status) === 'registration_open';

/** The host can still add an entry directly. */
export const canHostAddEntries = (league) =>
  ['draft', 'registration_open', 'registration_closed'].includes(normalizeLeagueStatus(league?.status));

export const isFinalStatus = (status) => ['completed', 'cancelled'].includes(normalizeLeagueStatus(status));

/** Options for a status select: the current status first, then where it can go. */
export const statusOptions = (status) => {
  const s = normalizeLeagueStatus(status);
  return [s, ...(LEAGUE_TRANSITIONS[s] || [])].map((value) => ({ value, label: leagueStatusLabel(value) }));
};
